import React from 'react'

import FunkGoContext from './FunkGoContext'

import * as singlePageAdapters from './adapters/singlePageAdapter'
import * as multiPageAdapters from './adapters/multiPageAdapters'
import * as multiPageServerAdapters from './adapters/multiPageServerAdapters'

import {
  withRouter,
} from 'react-router-dom'

import { createFunkGoPage } from './FunkGoPage'
import { createTransportContext } from './utils/transportContext'

/* --------------------------------------- */

const createInnerPage = (Component, { pluginHub, router }) => {
  const PageComponent = createFunkGoPage(Component, pluginHub, { skipPv: true })

  if (router) {
    return withRouter(PageComponent)
  }

  return PageComponent
}

const createAppContext = options => {
  return {
    _createPage: Component => createInnerPage(Component, options),
  }
}

/* --------------------------------------- */

const SINGLE_PAGE = 'SINGLE_PAGE'
const MULTI_PAGE = 'MULTI_PAGE'
const MULTI_PAGE_SERVER = 'MULTI_PAGE_SERVER'

const ADAPTERS = {
  [SINGLE_PAGE]: singlePageAdapters,
  [MULTI_PAGE]: multiPageAdapters,
  [MULTI_PAGE_SERVER]: multiPageServerAdapters,
}

const switchAdapter = ({ pluginHub, router }) => {
  const $detector = pluginHub.getDetector()

  // if (!router) {
  //   return ADAPTERS.SINGLE_PAGE
  // } else
  if ($detector.isServer()) {
    return ADAPTERS.MULTI_PAGE_SERVER
  }

  return ADAPTERS.MULTI_PAGE
}

/* --------------------------------------- */

const createRouter = rootOptions => switchAdapter(rootOptions).createRouter(rootOptions)

const createAppShell = rootOptions => {
  const pluginHub = rootOptions.pluginHub
  const $lifeCycle = pluginHub.getPlugin('lifeCycle')
  const AppShell = $lifeCycle ? $lifeCycle.getComponent('AppShell') : undefined

  if (!AppShell) {
    return props => props.children
  }

  return switchAdapter(rootOptions).createAppShell({
    ...rootOptions,
    AppShell,
  })
}

const createPageElements = (rootOptions) => switchAdapter(rootOptions).createPageElements(rootOptions)

/* --------------------------------------- */

const createFunkGoRoot = (pluginHub, router) => {
  const rootOptions = { pluginHub, router }

  /* --------------------------------------- */

  const appContext = createAppContext(rootOptions)
  const Router = createRouter(rootOptions)
  const AppShell = createAppShell(rootOptions)

  const PageElements = createPageElements(rootOptions)

  /* --------------------------------------- */

  return props => {
    const { location, routerContext, pageInfo, shellPropsFromServer } = props

    // location only works at server side
    // , need to pass location to Router for multi page mode (use StaticRouter)
    // , need to pass location to PageElements for single page mode (single page will ignore router)

    return (
      <FunkGoContext.Provider value={appContext}>
        <Router location={location} context={routerContext} pageInfo={pageInfo} shellPropsFromServer={shellPropsFromServer}>
          <AppShell location={location}>
            <PageElements location={location} {...pageInfo} ></PageElements>
          </AppShell>
        </Router>
      </FunkGoContext.Provider>
    )
  }
}

const matchPage = (location, pluginHub, router) => {
  if (!location || !location.pathname) {
    throw new Error('[FunkGoRoot] invalid parameter to match component')
  }
  const rootOptions = { pluginHub, router }
  const matchPage = switchAdapter(rootOptions).createMatchPage(rootOptions)

  const {
    Component,
    statusCode,
    match,
  } = matchPage(location)

  return {
    Component,
    statusCode,
    transportContext: createTransportContext({
      location,
      match,
    })
  }
}

/* --------------------------------------- */

export {
  createFunkGoRoot,
  matchPage
}